/**
 * Configuração da API
 * 
 * A URL da API é obtida através da variável de ambiente VITE_API_URL.
 * Se não estiver definida, usa um valor padrão baseado no ambiente.
 */

import { getToken } from './auth-storage';

const getApiUrl = (): string => {
  // Primeiro, verifica se VITE_API_URL está definida
  const viteApiUrl = import.meta.env.VITE_API_URL;
  
  if (viteApiUrl && viteApiUrl.trim() !== '') {
    // Remove barra final se existir
    return viteApiUrl.trim().replace(/\/$/, '');
  }

  // Fallback para desenvolvimento local
  if (import.meta.env.DEV) {
    console.warn('[API] VITE_API_URL não configurada, usando http://localhost:3000 como padrão para desenvolvimento');
    return 'http://localhost:3000';
  }

  // Em produção, se não estiver configurado, mostra erro no console
  console.error('[API] ERRO: VITE_API_URL não está configurada! Configure a variável de ambiente VITE_API_URL no arquivo .env');
  console.error('[API] Exemplo: VITE_API_URL=https://api.seudominio.com');
  
  // Em produção, retorna vazio para tentar usar mesma origem (não recomendado)
  // Mas é melhor falhar explicitamente
  return '';
};

export const API_URL = getApiUrl();

// Log da configuração (apenas em desenvolvimento)
if (import.meta.env.DEV) {
  console.log('[API] URL da API configurada:', API_URL || '(usando mesma origem - não recomendado)');
}

/**
 * Cria uma URL completa para um endpoint da API
 * @param endpoint - O endpoint da API (ex: '/api/tickets')
 * @returns URL completa para a API
 */
export const getApiEndpoint = (endpoint: string): string => {
  // Se o endpoint já começa com http, retorna como está
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }

  // Remove barra inicial se existir
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;

  // Se API_URL estiver vazia, tenta usar mesma origem (não recomendado)
  // Em produção, isso causará problemas de CORS
  if (!API_URL) {
    console.warn(`[API] VITE_API_URL não configurada, usando URL relativa: /${cleanEndpoint}`);
    console.warn('[API] Isso pode causar problemas de CORS. Configure VITE_API_URL no arquivo .env');
    return `/${cleanEndpoint}`;
  }

  // Combina API_URL com o endpoint
  const fullUrl = `${API_URL}/${cleanEndpoint}`;
  
  // Log em desenvolvimento e produção para debug (apenas URL, sem dados sensíveis)
  if (import.meta.env.DEV) {
    console.log(`[API] Requisição para: ${fullUrl}`);
  } else {
    // Em produção, log apenas se houver erro (será logado no catch)
    // Mas podemos logar a URL base para debug de configuração
    if (!API_URL || API_URL === '') {
      console.error('[API] ERRO: VITE_API_URL não configurada! URL da requisição será inválida.');
    }
  }
  
  return fullUrl;
};

/**
 * Função helper para fazer requisições fetch com autenticação
 */
export const apiFetch = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = getToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return fetch(getApiEndpoint(endpoint), {
    ...options,
    headers,
  });
};
